import { Grommet, ThemeType } from 'grommet'
import { useEffect, useState } from 'react'

import Icon from './components/Icon'
import { DarkThemeContext } from './contexts/darkThemeContext'

export const classicColors = {
    orange: '#EF9234',
    green: '#88A550',
    blue: '#219FD3',
    pink: '#E769A2',
    yellow: '#FBCD48',
    siftGreen: '#79F1B1',
    mintGreen: '#72dfd5',
    monitorBlue: '#5C9DD0',
    text: '#f8f8f8',
    dark6: '#999999',
}

export const newColors = {
    black: '#091924',
    darkPurple: '#1a1840',
    purple: '#29235c',
    blue: '#35a9e1',
    pink: '#a74c97',
    cyan: '#bfcdd7',
    green: '#08abae',
    orange: '#ea5d26',
    yellow: '#f9ae00',
    beige: '#efe0c3',
    white: '#eeeeee',
    lightGray: '#e0e0e0',
    darkGray: '#9f9f9f',
}

export type MapThemeType = {
    map?: {
        'site-icon': {
            fill?: string
            'fill-active'?: string
        }
        'site-extent': {
            fill?: string
            'fill-active'?: string
        }
        turbine: {
            fill?: string
            'fill-active'?: string
        }
        label: {
            color?: string
            background?: string
        }
    }
}

export type StylesThemeType = {
    styles?: {
        optionNotAvailable?: {
            fontStyle?: 'italic' | 'normal' | 'oblique'
            color: string
        }
    }
}

export type SiftMonitorThemeType = ThemeType & MapThemeType & StylesThemeType

export const classicTheme: SiftMonitorThemeType = {
    global: {
        colors: {
            brand: classicColors.pink,
            control: classicColors.mintGreen,
            focus: classicColors.mintGreen,
            text: classicColors.text,
            siftBlue: classicColors.blue,
            highlight: `${classicColors.mintGreen}42`,
            'accent-1': classicColors.mintGreen,
            'background-back': '#00212B',
            'background-contrast': '#002B36',
            'background-front': '#004052',
            'background-600': '#062d37',
            'background-500': '#09404e',
            'background-400': '#00485e',
            'dark-opacity': '#3333334f',
            'control-background': '#A1C2BF',
            warning: classicColors.orange,
        },
        font: {
            family: 'Urbanist',
            size: '14px',
        },
        drop: {
            background: { dark: 'background-contrast' },
            border: { radius: '3px' },
            zIndex: '150',
        },
        elevation: {
            dark: {
                small: '0px 4px 4px rgba(255, 255, 255, 0.02)',
            },
        },
        hover: {
            background: `${classicColors.mintGreen}30`,
        },
        control: {
            border: {
                color: 'control',
                radius: '8px',
            },
        },
    },
    heading: {
        level: {
            1: {
                medium: {
                    size: '42px',
                },
            },
        },
    },
    anchor: {
        color: 'control',
    },
    formField: {
        border: {
            side: 'all',
            color: 'focus',
        },
        round: '8px',
        error: {
            container: {
                basis: 'full',
                justify: 'center',
                align: 'center',
            },
            icon: <Icon icon='exclamation-triangle' color='warning' />,
        },
    },
    checkBox: {
        size: '1.4rem',
        border: {
            color: 'control',
        },
        toggle: {
            extend: ({ checked, theme }: any) => `
        ${checked && 'background-color: #DADADA22;'}
      `,
        },
    },
    button: {
        border: {
            radius: '24px',
        },
        primary: {
            extend: ({ theme }: any) =>
                `color: ${theme.global?.colors?.['background-back']};`,
        },
    },
    card: {
        container: {
            height: { min: '3rem' },
            border: { color: 'background-500' },
            margin: 'xsmall',
            background: 'background-back',
            round: 'xsmall',
        },
    },
    tabs: {
        gap: 'xxsmall',
        header: {
            alignSelf: 'start',
        },
        panel: {
            extend: ({ theme }: any) => `
        background: ${theme.global?.colors?.['background-contrast']};
        padding: ${theme.global?.edgeSize?.small};
    `,
        },
    },
    tab: {
        background: 'background-back',
        extend: `
    *{
        font-weight: 600;
        font-size: 18px;
    }
    border-radius: 6px 6px 0 0;
    `,
        pad: 'small',
        margin: undefined,
        border: undefined,
        hover: {
            // Annoying workaround to give active tabs a different hover style
            extend: ({ theme, background }: any) =>
                background === 'focus'
                    ? ''
                    : `background: ${theme.global?.colors?.highlight}`,
        },
        active: {
            background: 'focus',
            color: 'background-back',
        },
        color: 'text',
    },
    table: {
        header: {
            extend: '{border-width: 2px}',
        },
        body: {
            extend: '{border-color: rgba(255,255,255, 0.03)}',
        },
        row: {
            hover: {
                background: 'background-front',
            },
        },
    },
    tip: {
        content: {
            background: 'background-contrast',
            margin: 'small',
            round: '3px',
            elevation: 'xsmall',
        },
    },
    dataTable: {
        header: {
            font: {
                weight: 'bold',
            },
        },
        groupHeader: {
            background: 'background-contrast',
            border: {
                side: 'top',
                size: '3px',
            },
        },
        groupEnd: {
            border: {
                side: 'bottom',
                size: '3px',
            },
        },
    },
    text: {
        medium: {
            size: '14px',
        },
        small: {
            size: '12px',
        },
    },
    select: {
        options: {
            container: {
                pad: 'xsmall',
            },
        },
        clear: {
            container: {
                pad: 'small',
            },
        },
    },
    calendar: {
        day: {
            extend: ({ theme, isSelected }: any) =>
                `color: ${isSelected && theme?.global?.colors?.['background-back']};`,
        },
    },
    styles: {
        optionNotAvailable: { fontStyle: 'italic', color: newColors.darkGray },
    },
}

export const darkTheme: SiftMonitorThemeType = {
    ...classicTheme,
    global: {
        ...classicTheme.global,
        colors: {
            ...classicTheme.global!.colors,
            brand: '#F17242',
            control: newColors.green,
            focus: newColors.green,
            text: newColors.white,
            siftBlue: newColors.blue,
            highlight: `${newColors.green}42`,
            'accent-1': newColors.green,
            'background-back': newColors.black,
            'background-contrast': '#131D36',
            'background-front': '#191F42',
            'background-600': '#2A2865',
            'background-500': newColors.purple,
            'background-400': newColors.black,
            'dark-opacity': `${newColors.black}4f`,
            'control-background': newColors.blue,
            warning: newColors.yellow,
        },
    },
}

export const lightTheme: SiftMonitorThemeType = {
    ...classicTheme,
    global: {
        ...classicTheme.global,
        colors: {
            ...classicTheme.global!.colors,
            brand: newColors.pink,
            control: newColors.purple,
            focus: newColors.green,
            text: newColors.black,
            siftBlue: newColors.blue,
            highlight: `${newColors.green}42`,
            'accent-1': newColors.green,
            'background-back': newColors.white,
            'background-contrast': newColors.lightGray,
            'background-front': newColors.cyan,
            'background-600': newColors.lightGray,
            'background-500': newColors.cyan,
            'background-400': newColors.darkGray,
            'dark-opacity': `${newColors.black}4f`,
            'control-background': newColors.blue,
            warning: newColors.yellow,
        },
    },
}

export const highContrastTheme: SiftMonitorThemeType = {
    ...classicTheme,
    global: {
        ...classicTheme.global,
        colors: {
            ...classicTheme.global!.colors,
            brand: '#f0f',
            control: '#ff0',
            focus: '#ff0',
            text: '#fff',
            siftBlue: '#0ff',
            highlight: '#0ff',
            'accent-1': '#0ff',
            'background-back': '#000',
            'background-contrast': '#000',
            'background-front': '#000',
            'background-600': '#000',
            'background-500': '#000',
            'background-400': '#000',
            'dark-opacity': '#000',
            'control-background': '#000',
            warning: '#f00',
        },
    },
    card: {
        ...classicTheme.card,
        container: {
            ...classicTheme.card?.container,
            border: { color: '#fff' },
        },
    },
    table: {
        ...classicTheme.table,
        body: {
            ...classicTheme.table?.body,
            extend: `border: solid 1px white;`,
        },
    },
    tabs: {
        ...classicTheme.tabs,
        panel: {
            ...classicTheme.tabs?.panel,
            extend: `border: solid 1px white`,
        },
    },
    tab: {
        ...classicTheme.tab,
        border: { side: 'all', color: '#fff' },
    },
}

export const themes: Record<
    string,
    { theme: SiftMonitorThemeType; dark: boolean }
> = {
    light: { theme: lightTheme, dark: false },
    dark: { theme: darkTheme, dark: true },
    classic: { theme: classicTheme, dark: true },
    highContrast: { theme: highContrastTheme, dark: true },
}

export function getTheme() {
    const themeName = localStorage.getItem('theme') || 'auto'
    if (themeName === 'auto')
        return themes[
            window?.matchMedia?.('(prefers-color-scheme:dark)')?.matches
                ? 'dark'
                : 'light'
        ]
    return themes[themeName]
}

export function GrommetWrapper({ children }: any) {
    const [theme, setTheme] = useState(getTheme())

    useEffect(() => {
        window.addEventListener('themeChanged', () => {
            setTheme(getTheme())
        })
    }, [])

    return (
        <DarkThemeContext.Provider value={theme.dark}>
            <Grommet
                full
                theme={theme.theme}
                cssVars
                id='grommet-root'
                data-color-mode={theme.dark ? 'dark' : 'light'}
            >
                {children}
            </Grommet>
        </DarkThemeContext.Provider>
    )
}
